// CRM Organisation Detail — view org with interactions + linked items
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Globe, MapPin, Tag } from 'lucide-react'
import { getCrmOrg, getCrmInteractions, getCrmRelevantItems, getCrmContactOrgs, getCrmContact, createCrmContact, linkContactOrg } from '../../../services/api'
import type { CrmOrganisation, CrmInteraction, CrmRelevantItem, CrmContactOrg, CrmContact } from '../../../types'
import InteractionTimeline from '../../../components/crm/InteractionTimeline'
import InteractionForm from '../../../components/crm/InteractionForm'
import RelevantItemsList from '../../../components/crm/RelevantItemsList'
import ContactForm from '../../../components/crm/ContactForm'
import PageHeader from '../../../components/ui/PageHeader'
import { contactDisplayName } from '../../../utils/crmPipeline'

export default function OrgDetail() {
  const { projectId, orgId } = useParams<{ projectId: string; orgId: string }>()
  const [org, setOrg] = useState<CrmOrganisation | null>(null)
  const [interactions, setInteractions] = useState<CrmInteraction[]>([])
  const [relevantItems, setRelevantItems] = useState<CrmRelevantItem[]>([])
  const [linkedContacts, setLinkedContacts] = useState<CrmContactOrg[]>([])
  // CoWork Round 2 fix (Story #2777 #2788 follow-up): the linked-contacts list
  // previously rendered raw `contact_id` UUIDs because the CrmContactOrg link
  // record carries no name. Hydrate a contact-by-id map so rows show
  // "First Last" instead of "0c2b9c1c-...".
  const [contactsById, setContactsById] = useState<Record<string, CrmContact>>({})
  const [loading, setLoading] = useState(true)
  const [showInteractionForm, setShowInteractionForm] = useState(false)
  // Stream C4 (#2788): inline contact entry on the org page.
  const [showContactForm, setShowContactForm] = useState(false)
  const [contactSaveError, setContactSaveError] = useState('')
  const [tab, setTab] = useState<'interactions' | 'items' | 'contacts'>('interactions')

  useEffect(() => {
    if (!projectId || !orgId) return
    setLoading(true)
    Promise.all([
      getCrmOrg(orgId, projectId).catch(() => null),
      getCrmInteractions(projectId, orgId).catch(() => ({ interactions: [], count: 0 })),
      getCrmRelevantItems(projectId, orgId).catch(() => ({ relevant_items: [], count: 0 })),
      getCrmContactOrgs(projectId, undefined, orgId).catch(() => ({ contact_org_links: [], count: 0 })),
    ]).then(([o, i, r, c]) => {
      setOrg(o)
      setInteractions(i?.interactions || [])
      setRelevantItems(r?.relevant_items || [])
      const links: CrmContactOrg[] = c?.contact_org_links || []
      setLinkedContacts(links)
      setLoading(false)
      // Hydrate the contact-by-id cache so rows render names not UUIDs.
      // (CoWork R2 #7c) Best-effort: failures are silently ignored — the
      // row falls back to the raw contact_id, which is the prior behaviour.
      if (links.length && projectId) {
        Promise.all(
          links.map(l => getCrmContact(l.contact_id, projectId).catch(() => null))
        ).then(contacts => {
          const next: Record<string, CrmContact> = {}
          contacts.forEach(ct => {
            if (ct?.contact_id) next[ct.contact_id] = ct
          })
          setContactsById(next)
        })
      }
    })
  }, [projectId, orgId])

  // Format a linked contact's display name; fall back to UUID only if the
  // contact lookup is still in flight or genuinely missing.
  const displayContactName = (contactId: string): string => {
    const c = contactsById[contactId]
    if (!c) return contactId
    return contactDisplayName(c)
  }

  if (loading) return <div className="animate-pulse h-64 bg-sky-500/10 rounded-lg" />
  if (!org) return <p className="text-sm text-gray-500">Organisation not found</p>

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Link to={`/project/${projectId}/crm/organisations`} className="mt-1 shrink-0 text-gray-500 hover:text-gray-600"><ArrowLeft size={18} /></Link>
        <div className="flex flex-1 items-start gap-4 min-w-0">
          <div className="mt-1 h-10 w-10 shrink-0 bg-purple-500/10 rounded-lg flex items-center justify-center text-sm font-bold text-purple-700">{org.org_name?.[0]}</div>
          <div className="min-w-0 flex-1">
            <PageHeader
              title={org.org_name}
              description={org.industry || undefined}
              helpKey="crm"
            />
          </div>
        </div>
      </div>

      <div className="light-card p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {org.website && <div className="flex items-center gap-2 text-sm text-gray-600"><Globe size={14} className="text-gray-500" /> {org.website}</div>}
        {org.address && <div className="flex items-center gap-2 text-sm text-gray-600"><MapPin size={14} className="text-gray-500" /> {org.address}</div>}
        {org.domain && <p className="text-sm text-gray-600">Domain: {org.domain}</p>}
        {org.tags?.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Tag size={14} className="text-gray-500" />
            {org.tags.map(t => <span key={t} className="bg-sky-500/10 px-2 py-0.5 rounded text-xs">{t}</span>)}
          </div>
        )}
        {org.notes && <p className="text-sm text-gray-600 col-span-2">{org.notes}</p>}
      </div>

      <div className="flex gap-4 border-b border-sky-200/50">
        {(['interactions', 'items', 'contacts'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`pb-2 text-sm font-medium border-b-2 capitalize ${tab === t ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500'}`}>
            {t} ({t === 'interactions' ? interactions.length : t === 'items' ? relevantItems.length : linkedContacts.length})
          </button>
        ))}
      </div>

      {tab === 'interactions' && (
        <div>
          <button onClick={() => setShowInteractionForm(true)} className="mb-4 text-sm text-purple-600 hover:text-purple-700 font-medium">+ Add Interaction</button>
          <InteractionTimeline interactions={interactions} />
          {showInteractionForm && projectId && orgId && (
            <InteractionForm projectId={projectId} targetType="organisation" targetId={orgId}
              onClose={() => setShowInteractionForm(false)}
              onCreated={(i) => { setInteractions([i, ...interactions]); setShowInteractionForm(false) }} />
          )}
        </div>
      )}
      {tab === 'items' && <RelevantItemsList items={relevantItems} />}
      {tab === 'contacts' && (
        <div>
          {/* Stream C4 (#2788): "+ Add Contact" CTA — inline entry from the org page. */}
          <button
            data-testid="org-add-contact-button"
            onClick={() => { setContactSaveError(''); setShowContactForm(true) }}
            className="mb-4 text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            + Add Contact
          </button>
          {contactSaveError && (
            <p data-testid="org-add-contact-error" className="text-xs text-red-600 mb-2">{contactSaveError}</p>
          )}
          <div className="light-card divide-y divide-sky-200/30">
            {linkedContacts.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-500">No linked contacts</p>
            ) : (
              linkedContacts.map((lc) => (
                <Link key={`${lc.contact_id}-${lc.org_id}`} to={`/project/${projectId}/crm/contacts/${lc.contact_id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-white/30">
                  <p className="text-sm text-gray-700">{displayContactName(lc.contact_id)}</p>
                  {lc.role_at_org && <span className="text-xs text-gray-500">({lc.role_at_org})</span>}
                </Link>
              ))
            )}
          </div>
          {showContactForm && projectId && orgId && (
            <ContactForm
              // CoWork R2 #9c: pre-fill the Organisation field with the
              // current org's name so the user doesn't have to retype it.
              defaultOrgName={org.org_name}
              onClose={() => setShowContactForm(false)}
              onSubmit={async (data: Partial<CrmContact>) => {
                try {
                  const created = await createCrmContact({ ...data, project_id: projectId })
                  if (created?.contact_id) {
                    const link = await linkContactOrg({
                      contact_id: created.contact_id,
                      org_id: orgId,
                      project_id: projectId,
                    })
                    setLinkedContacts([...linkedContacts, link])
                    // Cache the contact details so the new row renders the
                    // name immediately instead of flashing a UUID until the
                    // background hydration fires (CoWork R2 #7c).
                    setContactsById(prev => ({ ...prev, [created.contact_id]: created }))
                  }
                  setShowContactForm(false)
                } catch (e) {
                  setContactSaveError((e as Error)?.message || 'Failed to create contact')
                  throw e
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
